/**
 * themeI18n.js — Ultra-thin i18n helper for theme chrome strings.
 *
 * Usage in any theme component:
 *    import { t } from "../_i18n/themeI18n";
 *    <h1>{t("Let's Plan Your Party")}</h1>
 *
 * How it works:
 *  - `t(englishText)` reads window.location.pathname synchronously.
 *  - If the path contains `/es` (either `/preview-xxx/es/...`, `/s/sub/es/...`,
 *    or just `/es/...` on a tenant subdomain), it looks up the Spanish
 *    translation in `themeDictEs`.
 *  - Falls back to the original English text if no translation is found.
 *
 * Re-rendering: React Router re-renders theme components whenever the URL
 * changes, so `t()` is re-evaluated and strings auto-swap on language toggle.
 *
 * Adding new strings: Just use `t("New String")` in a component and add the
 * translation to `themeDictEs.js`. If missing, English shows — zero breakage.
 */
import themeDictEs from "./themeDictEs";

/** Detect whether the visitor is on the Spanish version of a theme. */
export function isEsLocale() {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname || "";
  // Matches: /es, /es/, /es/anything, /preview-xxx/es..., /s/sub/es...
  return /(^|\/)es(\/|$)/.test(path);
}

/** Translate a theme chrome string. English passthrough on miss. */
export function t(englishText) {
  if (!englishText) return englishText;
  if (!isEsLocale()) return englishText;
  return themeDictEs[englishText] || englishText;
}

/** Shorthand for current theme language (for conditional rendering). */
export function themeLang() {
  return isEsLocale() ? "es" : "en";
}
